import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NetworkErrorAlert from '../components/NetworkErrorAlert';
import GoogleSignInButton from '../components/GoogleSignInButton';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();

  // Get the return URL from query parameters
  const returnUrl = searchParams.get('returnUrl');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [googleError, setGoogleError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    setSubmitError('');
  };

  const performLogin = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError('');
    setIsNetworkError(false);

    try {
      const result = await login(formData.email, formData.password, formData.rememberMe);

      // If there's a return URL, navigate there (after validating it's a relative path)
      if (returnUrl && returnUrl.startsWith('/')) {
        navigate(returnUrl);
        return;
      }

      // Otherwise, redirect based on role
      if (result.user.role === 'doctor') {
        navigate('/doctor/dashboard');
      } else if (result.user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (result.user.role === 'clinic_owner') {
        navigate('/clinic/dashboard');
      } else {
        navigate('/patient/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);

      // Check if this is a network error
      if (error.isNetworkError || error.name === 'NetworkError') {
        setIsNetworkError(true);
        setSubmitError(error.message);
      } else {
        setIsNetworkError(false);
        setSubmitError(error.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setIsSubmitting(false);
      setIsRetrying(false);
    }
  }, [formData, navigate, login, returnUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsNetworkError(false);

    // Basic validation
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await performLogin();
  };

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    await performLogin();
  }, [performLogin]);

  // Handle successful Google sign-in
  const handleGoogleSuccess = useCallback((data) => {
    setGoogleError('');

    // If there's a return URL, navigate there (after validating it's a relative path)
    if (returnUrl && returnUrl.startsWith('/')) {
      navigate(returnUrl);
      return;
    }

    // Otherwise, redirect based on user role
    if (data.user.role === 'doctor') {
      navigate('/doctor/dashboard');
    } else if (data.user.role === 'admin') {
      navigate('/admin/dashboard');
    } else if (data.user.role === 'clinic_owner') {
      navigate('/clinic/dashboard');
    } else {
      navigate('/patient/dashboard');
    }
  }, [navigate, returnUrl]);

  // Handle Google sign-in error
  const handleGoogleError = useCallback((errorMessage) => {
    setGoogleError(errorMessage);
  }, []);

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2 text-light-text dark:text-pure-white">
            Welcome Back
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Sign in to your Calendiary account
          </p>
        </div>

        {/* Login Form */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-8">
          {isNetworkError && submitError && (
            <NetworkErrorAlert
              onRetry={handleRetry}
              isRetrying={isRetrying}
              message={submitError}
            />
          )}

          {!isNetworkError && submitError && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" role="alert" aria-live="assertive">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                placeholder="john.doe@example.com"
                disabled={isSubmitting}
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'login-email-error' : undefined}
              />
              {errors.email && (
                <p id="login-email-error" className="mt-1 text-xs text-red-500" role="alert" aria-live="assertive">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`input-field ${errors.password ? 'border-red-500' : ''}`}
                placeholder="••••••••"
                disabled={isSubmitting}
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'login-password-error' : undefined}
              />
              {errors.password && (
                <p id="login-password-error" className="mt-1 text-xs text-red-500" role="alert" aria-live="assertive">{errors.password}</p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-light-border dark:border-border bg-light-bg dark:bg-obsidian focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-silver/20 focus:ring-offset-0 cursor-pointer"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-light-text-secondary dark:text-silver/60 group-hover:text-light-text dark:group-hover:text-silver transition-smooth">
                  Remember me
                </span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-light-border dark:border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-transparent text-light-text-secondary dark:text-silver/60 font-mono uppercase tracking-widest text-xs">
                or continue with
              </span>
            </div>
          </div>

          {/* Google Sign In Error */}
          {googleError && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" role="alert" aria-live="assertive">
              {googleError}
            </div>
          )}

          {/* Google Sign In Button */}
          <GoogleSignInButton
            mode="login"
            role="patient"
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            disabled={isSubmitting}
          />

          {/* Register Link */}
          <p className="mt-6 text-center text-light-text-secondary dark:text-silver/60 font-body text-sm">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-blue-600 dark:text-pure-white hover:underline transition-smooth"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
